/**
 * @author Vibhor <vibhor.sharma@314ecorp.com>
 * @description Code Hierarchy component
 */
import _ from 'lodash';
import clsx from 'clsx';
import { Card, Flex, Skeleton, Tree, Typography } from 'antd';
import { DataNode } from 'antd/es/tree';
import { DownC } from '@icon-park/react';
import { Link } from 'react-router-dom';

import useCodeHierarchy from 'hooks/codeExplorer/useCodeHierarchy';
import { CodeAvatar, CodeTitle, ImageWidget } from './CodeWidgets';
import { codeExplorerImages } from 'utils/codeExplorer/codeExplorerIcons';
import { getCodeDetailedViewPath } from 'utils/router';

const transformHierarchyToTreeData = (
	hierarchyData: CodeExplorer.ICodeHierarchy,
	matchingCodes: string[],
	showDotNotation = true,
	newTab = false,
): DataNode[] => {
	const treeData: DataNode[] = [];

	const buildTree = (nodeId: string): DataNode => {
		const nodeData = _.get(hierarchyData.data, nodeId);
		const children = _.get(hierarchyData.hierarchy, nodeId, []);
		const isMatchingCode = _.includes(matchingCodes, nodeData?.displaycode);

		const isLeafNode = _.isEmpty(children);
		const avatar = (
			<CodeAvatar
				key={nodeData?.code}
				code={nodeData?.code}
				hipaaSupport={isLeafNode}
				requiredCharLength={nodeData?.requiredcharlength}
			/>
		);

		const title = (
			<Flex align='center' gap={16}>
				{avatar}
				<CodeTitle code={showDotNotation ? nodeData?.displaycode : nodeData?.code} />
				<Link
					target={newTab ? '_blank' : '_self'}
					className='code-hierarchy-link'
					to={getCodeDetailedViewPath({ codeSet: nodeData?.codeset, code: nodeData?.displaycode })}
				>
					<Typography.Text className={clsx('py-1', { 'bg-[#FDF1C9]': isMatchingCode, 'bg-transparent': !isMatchingCode })}>
						{nodeData?.shortdescription}
					</Typography.Text>
				</Link>
			</Flex>
		);

		return {
			key: nodeData?.displaycode,
			title,
			children: _.size(children) > 0 ? _.map(children, (childId) => buildTree(childId)) : undefined,
		};
	};

	treeData.push(buildTree(hierarchyData.root));
	return treeData;
};

const CodeHierarchy: React.FC<{
	codeId: string;
	codeName?: string;
	matchingCodes?: string[];
	showDotNotation?: boolean;
	newTab?: boolean;
}> = ({ codeId, codeName, matchingCodes, showDotNotation = true, newTab = false }) => {
	const { data, isLoading } = useCodeHierarchy({ codeId });

	if (isLoading) {
		return (
			<Card className='p-2'>
				<Skeleton active />
			</Card>
		);
	}

	if (!data) {
		return (
			<Card className='p-2'>
				<ImageWidget
					data-testid='ce-hierarchy-no-data'
					imageSrc={codeExplorerImages.getAdditionalDetailedViewImage}
					description='No hierarchy data available. Please try again later.'
				/>
			</Card>
		);
	}

	const treeData = transformHierarchyToTreeData(data, matchingCodes, showDotNotation, newTab);

	return (
		<Tree
			data-testid='ce-hierarchy-tree'
			key={codeName}
			className='code-hierarchy-tree'
			treeData={treeData}
			selectable={false}
			switcherIcon={<DownC theme='multi-color' size='24' fill={['#FFFFFF', '#FFFFFF', '#595959', '#FFFFFF']} strokeWidth={3} />}
			defaultExpandedKeys={!_.isEmpty(matchingCodes) ? [...matchingCodes, codeName] : [codeName]}
		/>
	);
};
export default CodeHierarchy;
